import { GrowthBook } from '@growthbook/growthbook';

const READY_TIMEOUT_MS = 3000;

interface IGrowthBookWindowConfig {
    apiHost: string,
    clientKey: string,
    attributes?: Record<string, unknown>,
}

interface IEvalOptions {
    silent?: boolean,
}

declare global {
    interface Window {
        GROWTHBOOK?: IGrowthBookWindowConfig,
        astGrowthBook?: GrowthBook,
        astGrowthBookReady?: Promise<unknown>,
        astGrowthBookEval?: (mnemonic: string, options?: IEvalOptions) => Promise<string | null>,
        __astGbResolve?: (value?: unknown) => void,
        __astGbEval?: (mnemonic: string, options?: IEvalOptions) => Promise<string | null>,
        API_URL: string,
    }
}

/**
 * Registra a exposure direto no POST /api/metrics (mesmo contrato do window.setMetric
 * do legado), sem depender de globals de página — páginas ASTWeb não têm setMetric.
 * Identificação: X-UserID = attributes.id (usu_id, o mesmo do hash do sorteio);
 * anônimo cai no cookie CloudFlare que o serviço Metrics já usa como fallback.
 */
const trackExposure = (experimentKey: string, variationKey: string) => {
    const config = window.GROWTHBOOK;
    if (!config || !window.API_URL) return;

    const userId = String(config.attributes?.id || '');
    const headers: Record<string, string> = {
        'Client-Platform': String(config.attributes?.platform || ''),
        'X-Origin-Environment': String(config.attributes?.platform || '')
    };
    if (userId) headers['X-UserID'] = userId;

    fetch(`${window.API_URL}/metrics`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
            feature: `experiment/${experimentKey}`,
            category: experimentKey,
            action: 'exposure',
            value: variationKey
        })
    }).catch(() => undefined);
};

const waitReady = () => Promise.race([
    window.astGrowthBookReady,
    new Promise((resolve) => {
        setTimeout(resolve, READY_TIMEOUT_MS);
    })
]);

/**
 * Avalia um mnemonic no GrowthBook. Resolve o group-key ('cn'|'a'|'b'|...)
 * ou null quando a feature não existe lá — sinal de fallback pro serviço legado.
 *
 * `silent: true` (o fetchOnly do useExperiment) avalia numa instância-sombra sem
 * trackingCallback: mesmo hash → mesmo grupo, mas sem registrar exposure — leitura
 * pós-fato (ex.: página de sucesso) não pode enrolar o usuário no experimento.
 */
const evalExperimentGroup = async (mnemonic: string, options?: IEvalOptions): Promise<string | null> => {
    const growthbook = window.astGrowthBook;
    if (!growthbook) return null;

    await waitReady();

    const evaluator = options?.silent
        ? new GrowthBook({
            attributes: window.GROWTHBOOK?.attributes || {},
            features: growthbook.getFeatures()
        })
        : growthbook;

    const result = evaluator.evalFeature(mnemonic);
    if (evaluator !== growthbook) evaluator.destroy();

    if (result.source === 'unknownFeature' || result.value === null || result.value === undefined) return null;

    return String(result.value);
};

/**
 * Instancia o SDK compartilhado (window.astGrowthBook) a partir de window.GROWTHBOOK,
 * impresso pelos heads PHP (legado e ASTWeb). Idempotente — roda uma vez por página.
 */
export const initGrowthBook = () => {
    const config = window.GROWTHBOOK;
    if (!config || !config.clientKey || window.astGrowthBook) return;

    const growthbook = new GrowthBook({
        apiHost: config.apiHost,
        clientKey: config.clientKey,
        attributes: config.attributes || {},
        trackingCallback: (experiment, result) => {
            trackExposure(experiment.key, String(result.key));
        }
    });

    window.astGrowthBook = growthbook;
    window.__astGbEval = evalExperimentGroup;

    const ready = growthbook.init({ streaming: true }).catch(() => undefined);
    if (window.__astGbResolve) {
        // Bootstrap do head presente: resolve a promise que os consumidores já aguardam
        window.__astGbResolve(ready);
    } else {
        // Head sem bootstrap: comportamento antigo (consumidores pós-parse)
        window.astGrowthBookReady = ready;
        window.astGrowthBookEval = evalExperimentGroup;
    }
};
